import { useTranslation } from 'react-i18next';
import { ExternalLink, ShieldAlert } from 'lucide-react';
import type { RiskRegistryBlockData } from '@foco/contracts';

/**
 * Sanctions and debarment registers.
 *
 * This is the one block where an empty result is a *finding* rather than a gap,
 * and the wording says so ("not found in the lists searched") instead of a bare
 * "no data" — section 3.5 draws that distinction explicitly, because a user
 * reading "no data" cannot tell whether the company is clean or whether we never
 * asked.
 *
 * The lists that could not be searched are named underneath. A partial answer
 * presented as complete is exactly the failure this product must not have.
 */
export function RiskRegistryBlock({ data }: { data: unknown }): React.ReactNode {
  const { t } = useTranslation();
  const block = (data ?? {}) as RiskRegistryBlockData;
  const entries = block.entries ?? [];

  if (entries.length === 0) {
    return (
      <div>
        <p className="text-sm text-risk-low">
          {t('blocks.risk_registry.notFound')}
        </p>
        <p className="mt-1 text-xs text-[var(--color-muted-fg)]">
          {t('blocks.risk_registry.listsNote')}
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="flex items-center gap-2 text-sm font-semibold text-risk-high">
        <ShieldAlert aria-hidden className="h-4 w-4" />
        {t('blocks.risk_registry.total', { count: entries.length })}
      </p>

      <ul className="mt-3 divide-y divide-[var(--color-border)]">
        {entries.map((entry, index) => (
          <li key={`${entry.listCode}-${index}`} className="py-2 first:pt-0 last:pb-0">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <span className="text-sm font-medium">{entry.listName}</span>
              {entry.sanctionType && (
                <span className="rounded-full bg-risk-high/10 px-2 py-0.5 text-xs font-medium text-risk-high">
                  {entry.sanctionType}
                </span>
              )}
            </div>
            <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-[var(--color-muted-fg)]">
              {entry.dateFrom && (
                <span>
                  {t('blocks.risk_registry.dateFrom')}: {entry.dateFrom}
                </span>
              )}
              {entry.sourceUrl && (
                <a
                  href={entry.sourceUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-0.5 hover:text-brand-600 hover:underline"
                >
                  {t('lookup.openSource')}
                  <ExternalLink aria-hidden className="h-3 w-3" />
                </a>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
